import React from "react";
import { LOGO_OPTIONS } from "@/components/LogoOptions";
import { LOGO_OPTIONS_V2 } from "@/components/LogoOptionsV2";
import { ArrowLeft } from "lucide-react";

/**
 * Logo concept showcase.
 * Round 2 (Modular + Editorial, per user preference) is surfaced first.
 * Round 1 (original 8 directions) is kept below as reference.
 */

const LogoCard = ({ opt, index }) => {
  const { Component, id, name, tagline, description } = opt;
  return (
    <article
      data-testid={`logo-option-${id}`}
      className="group relative rounded-2xl border border-slate-800 bg-slate-900/50 overflow-hidden transition-all duration-300 hover:border-cyan-400/40"
    >
      <div className="flex items-center justify-between px-6 pt-5">
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-xs text-slate-600">
            {String(index + 1).padStart(2, "0")}
          </span>
          <h3 className="font-display text-xl font-bold tracking-tight text-slate-50">
            {name}
          </h3>
        </div>
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-cyan-400/80">
          {tagline}
        </span>
      </div>

      <div className="mt-5 mx-4 rounded-xl bg-slate-950 border border-slate-800 h-44 grid place-items-center overflow-hidden relative">
        <div className="absolute inset-0 bg-grid-fine opacity-40" />
        <div className="relative">
          <Component size={88} />
        </div>
      </div>

      <div className="mt-3 mx-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-slate-900 border border-slate-800 h-24 grid place-items-center">
          <Component size={36} />
        </div>
        <div
          className="rounded-xl h-24 grid place-items-center overflow-hidden relative"
          style={{
            background: "linear-gradient(135deg, #0F172A 0%, #083344 100%)",
            border: "1px solid rgba(34,211,238,0.25)",
          }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(34,211,238,0.25),transparent_65%)]" />
          <div className="relative">
            <Component size={36} />
          </div>
        </div>
      </div>

      <div className="mt-3 mx-4 rounded-xl border border-slate-800 bg-slate-950/80 px-5 py-4 flex items-center gap-3">
        <Component size={32} />
        <div className="flex flex-col leading-none">
          <span className="font-display text-sm font-semibold tracking-tight text-slate-100">
            Cleveland Creative
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400/80 mt-1">
            Group
          </span>
        </div>
      </div>

      <p className="px-6 py-5 text-sm text-slate-400 leading-relaxed">
        {description}
      </p>
    </article>
  );
};

const SectionHeader = ({ eyebrow, title, body }) => (
  <div className="mb-10 max-w-3xl">
    <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
      {eyebrow}
    </div>
    <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-[0.98] mb-5 text-balance">
      {title}
    </h2>
    {body && (
      <p className="text-slate-400 text-base md:text-lg leading-relaxed">
        {body}
      </p>
    )}
  </div>
);

const LogoShowcase = () => {
  const modular = LOGO_OPTIONS_V2.filter((o) => o.group === "Modular");
  const editorial = LOGO_OPTIONS_V2.filter((o) => o.group === "Editorial");

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-cyan-300 transition-colors text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to site
          </a>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400">
            Logo exploration · v2
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
        {/* Intro */}
        <div className="mb-16 md:mb-20 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-4">
            Round 2 · Modular + Editorial
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-[0.95] mb-6 text-balance">
            Eight deeper cuts in{" "}
            <span className="text-gradient-cyan">
              your two favorite lanes.
            </span>
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed">
            You picked <strong className="text-slate-100">Stack</strong>{" "}
            (modular) and <strong className="text-slate-100">Marque</strong>{" "}
            (editorial) — so these eight push further into those directions.
            Four modular systems built from rhythmic pieces, four typographic
            marks with serious editorial presence. Reply with a name and I'll
            deploy it site-wide.
          </p>
        </div>

        {/* Modular block */}
        <SectionHeader
          eyebrow="Modular · systems & geometry"
          title="Built from parts. Meant to fit."
          body="No letterforms — just structure. Each of these reads as an engineered system in miniature: stacked, layered, or modular by design."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-20 md:mb-24">
          {modular.map((opt, i) => (
            <LogoCard key={opt.id} opt={opt} index={i} />
          ))}
        </div>

        {/* Editorial block */}
        <SectionHeader
          eyebrow="Editorial · typographic marks"
          title="Letters, treated like artifacts."
          body="Wordmark-forward, confident, and ownable. These favor type over icon — the kind of mark you'd see on a monograph or a premium agency letterhead."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-20 md:mb-24">
          {editorial.map((opt, i) => (
            <LogoCard key={opt.id} opt={opt} index={i} />
          ))}
        </div>

        {/* How to choose */}
        <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-slate-900 to-slate-950 p-6 md:p-8 mb-20">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-cyan-400 mb-3">
            How to pick
          </div>
          <p className="text-slate-200 text-base leading-relaxed">
            Just reply with the name — e.g.{" "}
            <span className="font-mono text-cyan-300">"let's go with Lattice"</span>{" "}
            or{" "}
            <span className="font-mono text-cyan-300">"Stencil"</span>. I'll
            refine the details (optical alignment, stroke weights, micro
            animation on hover) and deploy it as the official brand mark across
            the site + admin + favicon.
          </p>
          <p className="text-slate-500 text-sm mt-3">
            Want to mix ideas? Say what you like from each — I can merge
            (e.g., "Offset modularity with Stencil typography").
          </p>
        </div>

        {/* Round 1 reference — collapsed below */}
        <details className="rounded-2xl border border-slate-800 bg-slate-900/30 p-6">
          <summary className="cursor-pointer text-sm text-slate-300 hover:text-cyan-300 font-medium select-none">
            View round 1 concepts (Aperture, Bolt, Stack, Marque, Node, Trace,
            Prism, Chevron)
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mt-6">
            {LOGO_OPTIONS.map((opt, i) => (
              <LogoCard key={opt.id} opt={opt} index={i} />
            ))}
          </div>
        </details>
      </main>

      <footer className="border-t border-slate-900 py-10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 text-xs text-slate-500 font-mono">
          © {new Date().getFullYear()} Cleveland Creative Group · Logo
          exploration
        </div>
      </footer>
    </div>
  );
};

export default LogoShowcase;
